// Pure outreach rules: send slots, follow-up dates, reply transitions, matching. No Deno globals, browser-safe.
import type { Rejection } from "./leadAgent.ts";

export type SendSettings = { days: number[]; hours: string; timezone: string };
export const DEFAULT_SEND: SendSettings = {
  days: [0, 1, 2, 3, 4],
  hours: "09:00-16:00",
  timezone: "Africa/Cairo",
};

const DAY_MS = 86_400_000;

type Wall = {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
};

const wallOf = (date: Date, timeZone: string): Wall => {
  const p: Record<string, number> = {};
  for (const part of new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(date)) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return {
    y: p.year,
    mo: p.month,
    d: p.day,
    h: p.hour,
    mi: p.minute,
    s: p.second,
  };
};

const wallMs = (w: Wall) => Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);

// Wall-clock time in a zone back to a real instant; two passes settle the offset across a DST change.
const fromWall = (w: Wall, timeZone: string): Date => {
  let t = wallMs(w);
  for (let i = 0; i < 2; i++) {
    const at = new Date(t);
    t =
      wallMs(w) -
      (wallMs(wallOf(at, timeZone)) - Math.floor(at.getTime() / 1000) * 1000);
  }
  return new Date(t);
};

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

export const nextSendSlot = (
  after: Date,
  settings: SendSettings = DEFAULT_SEND,
): Date => {
  const [from, to] = settings.hours.split("-").map(minutesOf);
  const w = wallOf(after, settings.timezone);
  const now = w.h * 60 + w.mi;
  for (let k = 0; k <= 7; k++) {
    const day = new Date(Date.UTC(w.y, w.mo - 1, w.d + k));
    if (!settings.days.includes(day.getUTCDay())) continue;
    if (k === 0 && now >= from && now < to) return after;
    if (k === 0 && now >= from) continue;
    return fromWall(
      {
        y: day.getUTCFullYear(),
        mo: day.getUTCMonth() + 1,
        d: day.getUTCDate(),
        h: Math.floor(from / 60),
        mi: from % 60,
        s: 0,
      },
      settings.timezone,
    );
  }
  return after; // ponytail: no allowed day in settings.days, nothing sensible to return
};

/** Midnight at the start of the day of `now` in the zone, as an instant */
export const startOfDay = (now: Date, timeZone: string): Date =>
  fromWall({ ...wallOf(now, timeZone), h: 0, mi: 0, s: 0 }, timeZone);

/** `n` send days before `now`, same wall-clock time (10 back from a Thursday, Sun-Thu, is two full weeks) */
export const minusWorkingDays = (
  now: Date,
  n: number,
  settings: SendSettings = DEFAULT_SEND,
): Date => {
  const w = wallOf(now, settings.timezone);
  let d = 0;
  // ponytail: a year back at most, so empty send days cannot loop forever
  while (n > 0 && d > -366) {
    d--;
    if (
      settings.days.includes(
        new Date(Date.UTC(w.y, w.mo - 1, w.d + d)).getUTCDay(),
      )
    )
      n--;
  }
  return fromWall({ ...w, d: w.d + d }, settings.timezone);
};

export const followupDueAt = (
  sentAt: Date,
  touch: number,
  delays: number[] = [4, 7],
  settings: SendSettings = DEFAULT_SEND,
): Date | null => {
  if (touch < 2 || touch > delays.length + 1) return null;
  // add calendar days on the wall clock so a DST change does not shift the hour
  const w = wallOf(sentAt, settings.timezone);
  const due = fromWall({ ...w, d: w.d + delays[touch - 2] }, settings.timezone);
  return nextSendSlot(due, settings);
};

export type Transition = {
  status: "replied" | "sent" | "closed";
  closed_reason?: string;
  needs_reply?: boolean;
  snoozed_until?: string;
  next_touch_at?: string | null;
  suppress?: boolean;
};

export const transitionFor = (
  input: {
    intent: string | null;
    is_bounce: boolean;
    received_at: string;
    returns_on?: string | null;
  },
  settings: SendSettings = DEFAULT_SEND,
): Transition => {
  if (input.is_bounce)
    return { status: "closed", closed_reason: "bounced", suppress: false };
  const received = new Date(input.received_at);
  switch (input.intent) {
    case "interested":
    case "question":
      return { status: "replied", needs_reply: true, next_touch_at: null };
    case "not_now":
      return {
        status: "replied",
        next_touch_at: null,
        snoozed_until: new Date(received.getTime() + 90 * DAY_MS).toISOString(),
      };
    case "not_interested":
      return {
        status: "closed",
        closed_reason: "not_interested",
        suppress: true,
      };
    case "unsubscribe":
      return {
        status: "closed",
        closed_reason: "unsubscribed",
        suppress: true,
      };
    case "out_of_office": {
      const back = input.returns_on
        ? new Date(input.returns_on)
        : new Date(received.getTime() + 7 * DAY_MS);
      return {
        status: "sent",
        next_touch_at: nextSendSlot(back, settings).toISOString(),
      };
    }
    default:
      return { status: "replied", needs_reply: true };
  }
};

export const isBounce = (msg: {
  from_email: string;
  subject: string;
  body: string;
}): boolean =>
  /^(postmaster|mailer-daemon)@/i.test(msg.from_email) ||
  /undeliverable|delivery (status )?notification|mail delivery failed/i.test(
    msg.subject,
  );

const QUOTE_MARKERS = [
  /^On .+ wrote:\s*$/m,
  /^From: .+$/m,
  /^-{3,} ?Original Message/im,
  /^_{10,}$/m,
];

export const stripQuoted = (text: string): { body: string; quoted: string } => {
  const cuts = QUOTE_MARKERS.map((re) => text.search(re)).filter((i) => i >= 0);
  if (!cuts.length) return { body: text, quoted: "" };
  const at = Math.min(...cuts);
  return { body: text.slice(0, at).trimEnd(), quoted: text.slice(at) };
};

export type Sent = {
  pre_lead_id: number;
  internet_message_id: string;
  conversation_id: string | null;
  contact_email: string;
  status: string;
};

export const LIVE = ["approved", "sent", "replied", "converted"];

export const matchInbound = (
  msg: { conversation_id?: string | null; from_email: string },
  sent: Sent[],
): Sent | null => {
  const byConversation = msg.conversation_id
    ? sent.find((s) => s.conversation_id === msg.conversation_id)
    : undefined;
  if (byConversation) return byConversation;
  const from = msg.from_email.toLowerCase();
  return (
    sent.find(
      (s) => LIVE.includes(s.status) && s.contact_email.toLowerCase() === from,
    ) ?? null
  );
};

export const rejectionsFrom = (
  removed: {
    company_name: string;
    domain?: string | null;
    industry?: string | null;
    segment_name?: string | null;
    removed_reason?: string | null;
  }[],
): Rejection[] =>
  removed.map((r) => ({
    name: r.company_name,
    domain: r.domain ?? undefined,
    industry: r.industry ?? undefined,
    segment: r.segment_name ?? undefined,
    reason: r.removed_reason ?? undefined,
  }));

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];
const MONTH = "(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?";

// `now` only decides the year of a date written without one: the next occurrence.
export const returnDateOf = (
  text: string,
  now: Date = new Date(),
): string | null => {
  const pad = (n: number) => String(n).padStart(2, "0");
  const iso = text.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return iso[0];
  const dm = text.match(
    new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}`, "i"),
  );
  const md = text.match(new RegExp(`\\b${MONTH}\\s+(\\d{1,2})\\b`, "i"));
  const [month, day] = dm
    ? [MONTHS.indexOf(dm[2].toLowerCase()), Number(dm[1])]
    : md
      ? [MONTHS.indexOf(md[1].toLowerCase()), Number(md[2])]
      : [-1, 0];
  if (month < 0 || day < 1 || day > 31) return null;
  let year = now.getUTCFullYear();
  if (Date.UTC(year, month, day) < now.getTime() - DAY_MS) year += 1;
  return `${year}-${pad(month + 1)}-${pad(day)}`;
};

// The touch-1 draft greeted and signed as the sender: the browser after drafting, the outreach function after a
// Claude rewrite.
export type Recipient = {
  email: string;
  first_name: string;
  last_name?: string;
  title?: string | null;
};

/** The person a pre-lead writes to, or null before one is found */
export const recipientOf = (row: {
  contact_email?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_title?: string | null;
}): Recipient | null =>
  row.contact_email
    ? {
        email: row.contact_email,
        first_name: row.contact_first_name ?? "",
        last_name: row.contact_last_name ?? undefined,
        title: row.contact_title,
      }
    : null;

export const isInbox = (r: Recipient) =>
  r.first_name === "General inbox" || !r.first_name;

export const GREETING = /^(hello|hi|dear|good (morning|afternoon))\b[^\n]*$/i;
const SIGN_OFF =
  /^(kind regards|best regards|warm regards|regards|best|sincerely|many thanks|thank you),?$/i;

/**
 * The draft addressed to this person and signed as the sender: "Dear Mona," (Hello, for a shared inbox) and the
 * sender's signature after the sign-off line.
 */
export const personaliseBody = (
  body: string,
  recipient: Recipient | null,
  signature: string,
) => {
  const greeting =
    recipient && !isInbox(recipient)
      ? `Dear ${recipient.first_name.trim()},`
      : "Hello,";
  const lines = body.trim().split("\n");
  const first = lines.findIndex((l) => l.trim());
  if (first >= 0 && GREETING.test(lines[first].trim())) lines[first] = greeting;
  else lines.unshift(greeting, "");
  let signOff = lines.length - 1;
  while (signOff >= 0 && !SIGN_OFF.test(lines[signOff].trim())) signOff--;
  const kept =
    signOff >= 0
      ? lines.slice(0, signOff + 1)
      : [...lines, "", "Kind regards,"];
  return [...kept, ...signature.trim().split("\n")].join("\n");
};

/** The signature for a sender, from Settings or the default ({name} is the signed-in user) */
export const signatureFor = (
  sender: "personal" | "sales",
  name: string,
  saved: { personal?: string; sales?: string },
) =>
  (
    (sender === "personal" ? saved.personal : saved.sales) ||
    (sender === "personal"
      ? "{name}\nCEO, THNK Consultancy\nwww.ithnk.com"
      : "THNK Consultancy\nsales@ithnk.com · www.ithnk.com")
  ).replace(/\{name\}/g, name);

/** Who an email shows as from: the sales address, or a user personally */
export type Sender = "sales" | "personal";

// C-level and senior managers answer a personal email from the CEO more often than one from a sales address.
// ponytail: a title word list; the sender stays editable per email.
export const SENIOR =
  /\b(ceo|cfo|cio|cto|coo|cmo|cdo|cso|cro|chief|president|chair(man|woman|person)?|founder|co-?founder|owner|managing director|general manager|gm|vp|svp|evp|vice president|director|head of|partner|minister|deputy minister|undersecretary|governor|board member)\b/i;

/** The sender for an email to someone with this title: personal for senior people, else the default */
export const senderFor = (
  title: string | null | undefined,
  fallback: Sender,
) => (title && SENIOR.test(title) ? "personal" : fallback);

// Who answers a consultancy email first: the technology and digital chiefs, then the chief executive, other chiefs,
// directors and heads, people, and shared inboxes last.
const RANKS: RegExp[] = [
  /\b(cio|cdo|cto|chief (information|digital|data|technology|transformation|strategy) officer|head of (it|digital|technology|transformation|strategy)|it director|director of (it|digital|technology|strategy))\b/i,
  /\b(ceo|chief executive|managing director|general manager|gm|president)\b/i,
  /\b(chief|vp|vice president|svp|evp)\b/i,
  /\b(director|head of|partner|minister)\b/i,
];
export const rankOf = (r: Recipient) => {
  if (isInbox(r)) return RANKS.length + 1;
  const rank = RANKS.findIndex((re) => re.test(r.title ?? ""));
  return rank < 0 ? RANKS.length : rank;
};

/** The person to write to: the best ranked, a named person before a shared inbox */
export const bestRecipient = <R extends Recipient>(recipients: R[]) =>
  [...recipients].sort((a, b) => rankOf(a) - rankOf(b))[0] ?? null;

/** The sender for a recipient: senior people get the user personally */
export const senderOf = (recipient: Recipient | null, fallback: Sender) =>
  recipient && !isInbox(recipient)
    ? senderFor(recipient.title, fallback)
    : fallback;

export type FoundContact = {
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  linkedin_url?: string;
};

const ROLE_BOXES: Record<string, string> = {
  info: "General inbox",
  contact: "General inbox",
  hello: "General inbox",
  enquiries: "General inbox",
  inquiries: "General inbox",
  sales: "Sales",
  marketing: "Marketing",
  hr: "Human resources",
  careers: "Human resources",
  jobs: "Human resources",
  support: "Support",
  cs: "Customer service",
  customerservice: "Customer service",
  media: "Media",
  pr: "Media",
  procurement: "Procurement",
  tenders: "Procurement",
  ceo: "CEO office",
  chairman: "Chairman office",
};

const capital = (word: string) =>
  word ? word[0].toUpperCase() + word.slice(1) : word;

/** A contact for a published address: a person when the address names one (ahmed.ali@), else its role */
export const contactFromEmail = (email: string): FoundContact => {
  const local = email.split("@")[0].toLowerCase();
  const role = ROLE_BOXES[local.replace(/[^a-z]/g, "")];
  if (role)
    return { email, first_name: role, last_name: "", title: "From website" };
  const [first, ...rest] = local
    .split(/[._-]+/)
    .filter((part) => /^[a-z]{2,}$/.test(part));
  // no name in it (bm19888@): a shared box
  if (!first)
    return {
      email,
      first_name: "General inbox",
      last_name: "",
      title: "From website",
    };
  return {
    email,
    first_name: capital(first),
    last_name: rest.map(capital).join(" "),
    title: "From website",
  };
};

/**
 * Whether a domain receives email (it has MX records), by Cloudflare's public DNS over HTTPS; unknown (DNS
 * unreachable) counts as yes, so a network hiccup does not drop real addresses. ponytail: no mailbox check (SMTP),
 * which browsers cannot do; Leads Finder's people come verified already.
 */
export const receivesMail = async (
  domain: string,
  fetchFn: typeof fetch = fetch,
): Promise<boolean> => {
  try {
    const response = await fetchFn(
      `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`,
      { headers: { Accept: "application/dns-json" } },
    );
    if (!response.ok) return true;
    const answer = (await response.json()) as {
      Status?: number;
      Answer?: { type: number }[];
    };
    // 3 = NXDOMAIN; type 15 = MX
    if (answer.Status === 3) return false;
    return (answer.Answer ?? []).some((a) => a.type === 15);
  } catch {
    return true;
  }
};

/** The people found worth writing to: one per address, not on the do-not-contact list, at a domain that receives mail */
export const usableContacts = async (
  found: FoundContact[],
  check: {
    isSuppressed: (email: string) => Promise<boolean>;
    receivesMail: (domain: string) => Promise<boolean>;
  },
) => {
  const mail = new Map<string, Promise<boolean>>();
  const seen = new Set<string>();
  const usable: FoundContact[] = [];
  for (const c of found) {
    const email = c.email.toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    if (await check.isSuppressed(email)) continue;
    const at = email.split("@")[1] ?? "";
    if (!mail.has(at)) mail.set(at, check.receivesMail(at));
    if (!(await mail.get(at))) continue;
    usable.push(c);
  }
  return usable;
};

/** The pre-lead's own contact (added by hand, or found by a people search) as a found person */
export const ownContact = (row: {
  contact_email?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_title?: string | null;
  contact_linkedin_url?: string | null;
}): FoundContact[] =>
  row.contact_email
    ? [
        {
          email: row.contact_email,
          first_name: row.contact_first_name ?? "",
          last_name: row.contact_last_name ?? "",
          title: row.contact_title ?? "",
          linkedin_url: row.contact_linkedin_url ?? undefined,
        },
      ]
    : [];

const REOPEN_DAYS = 180;

/** A company found again gets no new pre-lead while one is open, was removed by the User (whenever), or closed lately */
export const blocksNewPreLead = (
  same: {
    status: string;
    closed_reason?: string | null;
    closed_at?: string | null;
  }[],
  now: number,
) =>
  same.some(
    (p) =>
      p.status !== "closed" ||
      p.closed_reason === "removed" ||
      !p.closed_at ||
      new Date(p.closed_at).getTime() > now - REOPEN_DAYS * 86_400_000,
  );
